import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Text, Table, Thead, Tbody, Tr, Th, Td, HStack,
} from '@chakra-ui/react';
import { useMICUTheme } from '../../lib/micu/MICUThemeProvider';
import { getPatient } from '../../lib/micu/micuDatabase';
import { calculateIBW, calculateTidalVolumeRange } from '../../lib/micu/calculations';

const PEDIATRIC_AGE_THRESHOLD = 18;

const VentTidalVolumeInfo = ({ isOpen, onClose, caseId }) => {
  const { isDark } = useMICUTheme();
  const [patient, setPatient] = useState(null);
  const [vtRange, setVtRange] = useState(null);
  const [weightUsed, setWeightUsed] = useState(null);
  const [weightType, setWeightType] = useState('');
  const [formula, setFormula] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      const p = await getPatient(caseId);
      setPatient(p);

      if (!p) return;

      const isPediatric = p.ageUnit === 'months' || (p.ageUnit === 'years' && p.age < PEDIATRIC_AGE_THRESHOLD);

      if (isPediatric) {
        // Pediatric: use actual body weight
        if (p.weightKg) {
          setWeightUsed(p.weightKg);
          setWeightType('ABW');
          setVtRange(calculateTidalVolumeRange(p.weightKg));
          setFormula(`Using actual body weight: ${p.weightKg} kg`);
        }
      } else {
        // Adult: use IBW
        if (p.gender && p.heightInches) {
          const ibw = calculateIBW(p.gender, p.heightInches);
          setWeightUsed(ibw);
          setWeightType('IBW');
          setVtRange(calculateTidalVolumeRange(ibw));
          const base = p.gender === 'M' ? 50 : 45.5;
          setFormula(`IBW = ${base} + 2.3 \u00d7 (${p.heightInches}" - 60) = ${ibw} kg`);
        }
      }
    })();
  }, [isOpen, caseId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent bg={isDark ? 'micu.bgSecondary' : 'micu.bg'} color="micu.text" mx={4}>
        <ModalHeader fontSize="md">Tidal Volume Reference</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {!patient ? (
            <Text fontSize="sm" color="micu.textMuted">No patient data. Add demographics first.</Text>
          ) : !vtRange ? (
            <Text fontSize="sm" color="micu.textMuted">
              Need {patient?.gender ? '' : 'gender, '}{patient?.heightInches ? '' : 'height '} to calculate.
            </Text>
          ) : (
            <>
              <HStack spacing={2} mb={3}>
                <Text fontSize="sm" color="micu.textSecondary">
                  Weight used: <Text as="span" fontWeight="bold" color="micu.teal">{weightUsed} kg ({weightType})</Text>
                </Text>
              </HStack>

              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      {[4, 5, 6, 7, 8].map(cc => (
                        <Th key={cc} color="micu.textSecondary" borderColor="micu.border" textAlign="center" px={2}>
                          {cc} cc/kg
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      {[4, 5, 6, 7, 8].map(cc => (
                        <Td key={cc} borderColor="micu.border" textAlign="center" fontWeight="bold" color="micu.text" px={2}>
                          {vtRange[cc]} mL
                        </Td>
                      ))}
                    </Tr>
                  </Tbody>
                </Table>
              </Box>

              <Text fontSize="xs" color="micu.textMuted" mt={3}>{formula}</Text>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default VentTidalVolumeInfo;
